
import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule, inject, provideAppInitializer } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { environment } from '../environments/environment';
import { HomeComponent } from './home/home.component';
import { SignatureFormComponent } from './signature-form/signature-form.component';
import { ReactiveFormsModule } from '@angular/forms';
import { API_BASE_URL, ClientApi } from './client-api.service';
import { SignatureRendererComponent } from './signature-renderer/signature-renderer.component';
import { ConfigService } from './config.service';
import { AUTH_CONFIG, AuthConfig, normalizeScopesArray, SESSION_POST_LOGIN_REDIRECT_URL } from './auth/auth-config';
import { MSAL_GUARD_CONFIG,
         MSAL_INSTANCE,
         MSAL_INTERCEPTOR_CONFIG,
         MsalBroadcastService,
         MsalGuard,
         MsalGuardConfiguration,
         MsalInterceptor,
         MsalInterceptorConfiguration,
         MsalService} from '@azure/msal-angular';
import { BrowserCacheLocation,
         BrowserUtils,
         Configuration,
         InteractionType,
         IPublicClientApplication,
         LogLevel,
         PublicClientApplication } from '@azure/msal-browser';
//import { TitleRouteSyncerModule } from './title-route-syncer/title-route-syncer.module';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { AuthService } from './auth/auth.service';
import { NotificationsComponent, NotificationsService } from './shared/notifications';
import { UserStatusComponent } from './auth/user-status.component';
import { PreloaderComponent } from './shared/preloader.component';
import { SimplePanelComponent } from './shared/simple-panel/simple-panel.component';
import { ScrollService } from './scroll.service';
import { SimplePanelService } from './shared/simple-panel/simple-panel.service';
import { TemplateUtilService } from './shared/template-util.service';
import { LoadingOverlayComponent } from './shared/loading-overlay.component';
import { CommonModule } from '@angular/common';
import { SimpleMenuComponent } from './shared/simple-panel/simple-menu.component';
import { ImageListComponent } from './maintenance/image-list/image-list.component';
import { MaintenanceComponent } from './maintenance/maintenance.component';
import { ImageThumbnailComponent } from './maintenance/image-thumbnail/image-thumbnail.component';
import { ImagePopupComponent } from './maintenance/image-popup/image-popup.component';
import { AccordionComponent } from './shared/accordion/accordion.component';
import { AccordionGroupComponent } from './shared/accordion/accordion-group.component';
import { GMapComponent } from './shared/accordion/map.component';
import { ImageUploadComponent } from "./maintenance/image-upload/image-upload.component";
import { DragDropDirective } from './directives/drap-drop.directive';


// disable the auth if in an iframe or popup
const AUTH_DISABLED = BrowserUtils.isInIframe() || BrowserUtils.isInPopup();

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

function msalInstanceFactory(authConfig: AuthConfig): IPublicClientApplication {
  const config: Configuration = {
    auth: {
      authority: environment.msalConfig.auth.authority || 'https://login.microsoftonline.com/f5c71729-7816-4984-867a-0b3a69f99ff8/',
      clientId: environment.msalConfig.auth.clientId,
      redirectUri: '/',
      postLogoutRedirectUri: '/',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    }
  };
  if (authConfig?.logLevel) {
    // if config.system is set to undefined it throws an error.  That is why it is added here.
    config.system = {
      loggerOptions: {
        loggerCallback: (_, message) => console.log(message),
        logLevel: authConfig?.logLevel || 2,
        piiLoggingEnabled: false
      }
    };
  }
  return new PublicClientApplication(config);
}

function msalInterceptorConfigFactory(authConfig: AuthConfig): MsalInterceptorConfiguration {
  // essentially disable the interceptor by not providing a resource map
  return AUTH_DISABLED
    ? { interactionType: InteractionType.Popup, protectedResourceMap: new Map() }
    : {
      interactionType: InteractionType.Redirect,
      protectedResourceMap: new Map(Array.from(authConfig.protectedResourceMap.entries())
        .map(([res, scopesOrEmpty]) => [res, normalizeScopesArray(scopesOrEmpty)]))
    };
}

function msalGuardConfigFactory(authConfig: AuthConfig): MsalGuardConfiguration {
  const scopes = Array.from(authConfig.protectedResourceMap.values())
    .filter((x): x is NonNullable<typeof x> => !!x) // remove null values.
    .reduce((acc, cur) => acc.concat(cur), [] as string[]) // flatten arrays
    .reduce((acc, cur) => acc.includes(cur) ? acc : acc.concat([cur]), [] as string[]); // make distinct

  return AUTH_DISABLED
      ? { interactionType: InteractionType.Popup }
      : {
        interactionType: InteractionType.Redirect,
        authRequest: (_, route) => {
          sessionStorage.setItem(SESSION_POST_LOGIN_REDIRECT_URL, route.url);
          return {
            scopes
          };
        },
        loginFailedRoute: authConfig?.unauthorizedRoute || '/auth/unauthorized'
      };
}


@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    LoadingOverlayComponent,
    NotificationsComponent,
    PreloaderComponent,
    SignatureFormComponent,
    SignatureRendererComponent,
    SimplePanelComponent,
    SimpleMenuComponent,
    UserStatusComponent,
    MaintenanceComponent,
    ImageListComponent,
    ImageThumbnailComponent,
    ImagePopupComponent,
    AccordionGroupComponent,
    AccordionComponent,
    GMapComponent,
    DragDropDirective,
    ImageUploadComponent
  ],
  imports: [
    BrowserModule,
    CommonModule,
    AppRoutingModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    HttpClientModule,
    
],
  providers: [
    AuthService,
    ConfigService,
    provideAppInitializer(() => {
        const initializerFn = ((configService: ConfigService) => () => configService.load(environment))(inject(ConfigService));
        return initializerFn();
      }),
    {
      provide: API_BASE_URL,
      deps: [ConfigService, APP_INITIALIZER],
      useFactory: (configService: ConfigService) =>
        configService?.get('apiBaseUrl'),
    },
    {
      provide: AUTH_CONFIG,
      deps: [ConfigService, APP_INITIALIZER],
      useFactory: (configService: ConfigService) =>
        configService?.getAuthConfig()
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: msalInstanceFactory,
      deps: [AUTH_CONFIG]
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: msalGuardConfigFactory,
      deps: [AUTH_CONFIG]
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: msalInterceptorConfigFactory,
      deps: [AUTH_CONFIG]
    },
    ClientApi,
    MsalGuard,
    MsalService,
    MsalBroadcastService,
    NotificationsService,
    SimplePanelService,
    ScrollService,
    TemplateUtilService,
    { provide: Window, useValue: window },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: MsalInterceptor,
        multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
